import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import { getPatientDocumentFileUrl } from 'components/clinical-data/documentUtils';
import { prepareComments } from 'components/clinical-data/PatientDocumentComments';
import { DocumentActionButton } from 'components/clinical-data/RequiresActionDocumentList';
import LoaderComponent from 'components/LoaderComponent';
import PatientMedicalDocumentPdfModal from 'components/pdf-modal/PatientMedicalDocumentPdfModal';
import React, { useRef, useState } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { getCdrDocumentFiles } from 'services/patientRecordServices';
import { CdrDocumentFileContent, PatientCdrDocumentDto, UserDisplayDto } from 'ts-types/api.types';
import { withRouterWorkaround } from 'utils/workaroundUtils';

interface CdrDocumentFileContentExtended extends CdrDocumentFileContent {
  patientId?: number;
}

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
  document: PatientCdrDocumentDto;
  currentUserDisplayData: UserDisplayDto;
  iconTooltip: string;
  handleError: (errorData: any) => void;
  onClose: (documentId: number) => void;
}

const CdrDocumentPdfModal = (props: Props) => {

  const cancelTokenSource = useRef(axios.CancelToken.source());

  const [loadingData, setLoadingData] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);

  const [
    patientMedicalDocumentPdfResponse,
    setPatientMedicalDocumentPdfResponse
  ] = useState(undefined as CdrDocumentFileContentExtended[] | undefined);

  const openModal = async () => {

    const {
      document
    } = props;

    setLoadingData(true);
    setModalOpen(true);

    try {

      const response = await getCdrDocumentFiles(
          document.patientId, document.documentId, cancelTokenSource.current);

      const extendedDocFiles: CdrDocumentFileContentExtended[] = (response || []).map(
          docFile => ({
            ...docFile,
            patientId: document.patientId
          }));

      setPatientMedicalDocumentPdfResponse(extendedDocFiles);
    } catch (e) {
      setModalOpen(false);

      e.response
      && e.response.data
      && props.handleError
      && props.handleError(e.response.data);
    } finally {
      setLoadingData(false);
    }
  }

  const closeModal = () => {
    const {document, onClose} = props;
    onClose && onClose(document.documentId);
    setModalOpen(false);
  };

  if (loadingData) {
    return <LoaderComponent message="loading"/>;
  }

  const firstDocumentPdfDto: CdrDocumentFileContentExtended | undefined =
      patientMedicalDocumentPdfResponse && patientMedicalDocumentPdfResponse.length > 0
          ? patientMedicalDocumentPdfResponse![0]
          : undefined;

  return <>
    <DocumentActionButton
        iconName="print"
        popupMessage={props.iconTooltip}
        onClick={openModal}
    />

    {
        modalOpen
        && <PatientMedicalDocumentPdfModal
            pdfResponses={patientMedicalDocumentPdfResponse}
            modalOpen={modalOpen}
            onCloseModal={closeModal}
            getPdfUrl={getPatientDocumentFileUrl}
            textContent={firstDocumentPdfDto!.textContent || ""}
            comments={prepareComments(firstDocumentPdfDto!.comments || [])}
            currentUserDisplay={props.currentUserDisplayData}
            patientId={firstDocumentPdfDto!.patientId}
            documentId={firstDocumentPdfDto!.documentId}
        />
    }
  </>;
}

export default withRouterWorkaround(
    withMessages(
        withAuthContext(
            CdrDocumentPdfModal)));
